import { useState, useRef, useEffect } from 'react';
import { Monitor, MicOff, Mic, PhoneOff, User } from 'lucide-react';
import styles from './screen-share.module.css';


export default function ScreenShare({userId, activeChat, onClose, onMinimize  }) {
  const [targetUserId, setTargetUserId] = useState(activeChat?._id || '');
  const [isConnected, setIsConnected] = useState(false);
  const [inCall, setInCall] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [incomingCall, setIncomingCall] = useState(null);
  const [callStatus, setCallStatus] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const localScreenRef = useRef(null);
  const remoteScreenRef = useRef(null);
  const socketRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const screenStreamRef = useRef(null);
  const audioStreamRef = useRef(null);

  const iceServers = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ]
  };

  const connectToServer = () => {
    if (!userId.trim()) {
      alert('Please enter a User ID');
      return;
    }

    // Simulated Socket.IO for demo
    // Replace with: socketRef.current = io('your-server-url');
    socketRef.current = {
      emit: (event, data) => console.log('Emit:', event, data),
      on: (event, callback) => console.log('Listening to:', event),
      connected: true
    };

    setIsConnected(true);
    setCallStatus('Connected to server');
    setupSocketListeners();
  };

  const setupSocketListeners = () => {
    // In production, implement these socket listeners:
    // socketRef.current.on('call-offer', handleReceiveOffer);
    // socketRef.current.on('call-answer', handleReceiveAnswer);
    // socketRef.current.on('ice-candidate', handleReceiveIceCandidate);
    // socketRef.current.on('call-ended', handleCallEnded);
  };

  const startScreenShare = async () => {
    try {
      // Get screen share
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          displaySurface: 'monitor'
        },
        audio: false
      });

      screenStreamRef.current = screenStream;

      // Get audio separately
      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: false
      });

      audioStreamRef.current = audioStream;

      // Display local screen share
      if (localScreenRef.current) {
        localScreenRef.current.srcObject = screenStream;
      }

      // Handle when user stops sharing via browser UI
      screenStream.getVideoTracks()[0].onended = () => {
        endCall();
      };

      return { screenStream, audioStream };
    } catch (err) {
      console.error('Error accessing screen/audio:', err);
      setCallStatus('Error: Cannot access screen or microphone');
      throw err;
    }
  };

  const createPeerConnection = () => {
    const peerConnection = new RTCPeerConnection(iceServers);

    // Add screen share video track
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => {
        peerConnection.addTrack(track, screenStreamRef.current);
      });
    }

    // Add audio track
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => {
        peerConnection.addTrack(track, audioStreamRef.current);
      });
    }

    // Handle incoming tracks
    peerConnection.ontrack = (event) => {
      if (remoteScreenRef.current) {
        if (!remoteScreenRef.current.srcObject) {
          remoteScreenRef.current.srcObject = new MediaStream();
        }
        remoteScreenRef.current.srcObject.addTrack(event.track);
      }
    };

    // Handle ICE candidates
    peerConnection.onicecandidate = (event) => {
      if (event.candidate && socketRef.current) {
        socketRef.current.emit('ice-candidate', {
          candidate: event.candidate,
          to: targetUserId
        });
      }
    };

    // Monitor connection state
    peerConnection.onconnectionstatechange = () => {
      setCallStatus(`Connection: ${peerConnection.connectionState}`);
      if (peerConnection.connectionState === 'connected') {
        setCallStatus('Screen sharing active');
      }
    };

    return peerConnection;
  };

  const startCall = async () => {
    if (!targetUserId.trim()) {
      alert('Please enter target User ID');
      return;
    }

    try {
      setCallStatus('Starting screen share...');
      await startScreenShare();
      setIsSharing(true);
      
      peerConnectionRef.current = createPeerConnection();
      
      const offer = await peerConnectionRef.current.createOffer();
      await peerConnectionRef.current.setLocalDescription(offer);
      
      socketRef.current.emit('call-offer', {
        offer: offer,
        from: userId,
        to: targetUserId
      });
      
      setInCall(true);
      setCallStatus('Sharing screen...');
    } catch (err) {
      console.error('Error starting screen share:', err);
      setCallStatus('Failed to start screen share');
    }
  };

  const handleReceiveOffer = async (data) => {
    setIncomingCall(data);
    setCallStatus(`Incoming screen share from ${data.from}`);
  };

  const acceptCall = async () => {
    if (!incomingCall) return;

    try {
      setCallStatus('Accepting screen share...');
      
      peerConnectionRef.current = createPeerConnection();
      
      await peerConnectionRef.current.setRemoteDescription(
        new RTCSessionDescription(incomingCall.offer)
      );
      
      const answer = await peerConnectionRef.current.createAnswer();
      await peerConnectionRef.current.setLocalDescription(answer);
      
      socketRef.current.emit('call-answer', {
        answer: answer,
        to: incomingCall.from
      });
      
      setInCall(true);
      setIncomingCall(null);
      setTargetUserId(incomingCall.from);
      setCallStatus('Receiving screen share');
    } catch (err) {
      console.error('Error accepting call:', err);
      setCallStatus('Failed to accept screen share');
    }
  };

  const endCall = () => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }

    if (localScreenRef.current) {
      localScreenRef.current.srcObject = null;
    }
    if (remoteScreenRef.current) {
      remoteScreenRef.current.srcObject = null;
    }

    if (socketRef.current && inCall) {
      socketRef.current.emit('call-ended', { to: targetUserId });
    }

    setInCall(false);
    setIsSharing(false);
    setIncomingCall(null);
    setCallStatus('Screen share ended');
  };

  const toggleAudio = () => {
    if (audioStreamRef.current) {
      const audioTrack = audioStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioEnabled(audioTrack.enabled);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
      }
    };
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.maxWidth}>
        <div className={styles.controlPanel}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
          <h1 className={styles.title}>Screen Share with Audio</h1>
          <div style={{ display: 'flex', gap: '8px' }}>
              {/* Minimize button */}
              <button
                onClick={onMinimize}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
                title="Minimize"
              >
                −
              </button>
              
              {/* Close button */}
              <button
                onClick={onClose}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
                title="Close"
              >
                ×
              </button>
            </div>
          </div>
          <p className={styles.subtitle}>WebRTC + Socket.IO Integration</p>

          <div className={styles.divider} />
          {!isConnected ? (
            <div className={styles.formGroup}>
              <div>
                <label className={styles.label}>Your User ID:{userId}</label>
                {/* <input
                  type="text"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="Enter your user ID (e.g., user1)"
                  className={styles.input}
                /> */}
              </div>
              <button onClick={connectToServer} className={styles.btnPrimary}>
                Connect to Server
              </button>
            </div>
          ) : (
            <div className={styles.formGroup}>
              <div className={styles.statusConnected}>
                <p className={styles.statusText}>
                  <User size={20} />
                  Connected as: {userId}
                </p>
              </div>

              {callStatus && (
                <div className={styles.statusInfo}>
                  <p className={styles.statusInfoText}>{callStatus}</p>
                </div>
              )}

              {!inCall && !incomingCall && (
                <div>
                  <label className={styles.label}>Share Screen With:{targetUserId}</label>
                  <div className={styles.inputGroup}>
                    {/* <input
                      type="text"
                      value={targetUserId}
                      onChange={(e) => setTargetUserId(e.target.value)}
                      placeholder="Enter user ID to share with"
                      className={styles.input}
                    /> */}
                    <button onClick={startCall} className={styles.btnShare}>
                      <Monitor size={20} />
                      Share Screen
                    </button>
                  </div>
                </div>
              )}

              {incomingCall && (
                <div className={styles.incomingCall}>
                  <p className={styles.incomingCallText}>
                    {incomingCall.from} wants to share their screen
                  </p>
                  <div className={styles.incomingCallActions}>
                    <button onClick={acceptCall} className={styles.btnAccept}>
                      View Screen
                    </button>
                    <button onClick={() => setIncomingCall(null)} className={styles.btnDecline}>
                      Decline
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.videoGrid}>
          {isSharing && (
            <div className={styles.videoCard}>
              <div className={styles.videoHeaderPrimary}>
                <Monitor size={20} />
                Your Screen Share
              </div>
              <div className={styles.videoContainer}>
                <video
                  ref={localScreenRef}
                  autoPlay
                  muted
                  playsInline
                  className={styles.video}
                />
              </div>
            </div>
          )}

          {/* <div className={styles.videoCard}>
            <div className={styles.videoHeader}>
              <Monitor size={20} />
              Remote Screen Share
            </div>
            <div className={styles.videoContainer}>
              <video
                ref={remoteScreenRef}
                autoPlay
                playsInline
                className={styles.video}
              />
              {!inCall && (
                <div className={styles.videoPlaceholder}>
                  <p className={styles.videoPlaceholderText}>Waiting for screen share...</p>
                </div>
              )}
            </div>
          </div> */}
        </div>

        {inCall && (
          <div className={styles.controlsCard}>
            <div className={styles.controls}>
              <button
                onClick={toggleAudio}
                className={isAudioEnabled ? styles.btnControl : styles.btnControlMuted}
                title={isAudioEnabled ? 'Mute Microphone' : 'Unmute Microphone'}
              >
                {isAudioEnabled ? <Mic size={24} /> : <MicOff size={24} />}
              </button>
              
              <button
                onClick={endCall}
                className={styles.btnEnd}
                title="Stop Sharing"
              >
                <PhoneOff size={24} />
              </button>
            </div>
          </div>
        )}

        {/* <div className={styles.guideCard}>
          <h2 className={styles.guideTitle}>Quick Setup Guide</h2>
          <div className={styles.guideContent}>
            <p>
              <strong>1. Replace Socket.IO simulation with real connection:</strong>
            </p>
            <code className={styles.codeBlock}>
              import io from 'socket.io-client';<br/>
              const socket = io('https://your-server.com');
            </code>
            
            <p>
              <strong>2. Server-side Socket.IO events (Node.js):</strong>
            </p>
            <code className={styles.codeBlock}>
{`io.on('connection', (socket) => {
  socket.on('call-offer', (data) => {
    io.to(data.to).emit('call-offer', data);
  });
  
  socket.on('call-answer', (data) => {
    io.to(data.to).emit('call-answer', data);
  });
  
  socket.on('ice-candidate', (data) => {
    io.to(data.to).emit('ice-candidate', data);
  });
  
  socket.on('call-ended', (data) => {
    io.to(data.to).emit('call-ended', data);
  });
});`}
            </code>
            
            <p>
              <strong>3. What's included:</strong>
            </p>
            <ul className={styles.list}>
              <li>Screen sharing with audio</li>
              <li>Mute/unmute microphone</li>
              <li>Accept/decline incoming shares</li>
              <li>Automatic cleanup when user stops sharing</li>
            </ul>

            <p>
              <strong>4. Browser permissions:</strong>
            </p>
            <ul className={styles.list}>
              <li>Screen sharing requires user interaction (button click)</li>
              <li>Browser will show screen selection dialog</li>
              <li>Microphone permission will be requested separately</li>
            </ul>
          </div>
        </div>  */}
      </div>
    </div>
  );
}
